import { useLocation } from 'react-router';
import SEO from './SEO';
import statutesSeo from '../data/statutes-seo.json';
import {
  STATUTES_INDEX_SEO,
  buildStatuteJsonLdBlocks,
  buildStatuteSeoDescription,
  buildStatuteSeoTitle,
} from '../lib/statuteSeoCopy';

export type StatuteSeoEntry = {
  id: string;
  title: string;
  description: string;
  canonical: string;
  displayTitle: string;
  year: number | null;
  category: string | null;
  jurisdiction: string;
  sectionCount: number;
};

const STATUTES = statutesSeo as Record<string, StatuteSeoEntry>;

function normalizePathname(path: string): string {
  if (!path || path === '/') return '/';
  return path.endsWith('/') ? path.replace(/\/+$/, '') || '/' : path;
}

function resolveStatuteSeo(statute: StatuteSeoEntry) {
  return {
    title: buildStatuteSeoTitle(statute.displayTitle, statute.year),
    description: buildStatuteSeoDescription(
      statute.displayTitle,
      statute.year,
      statute.sectionCount,
    ),
    canonical: statute.canonical,
    jsonLd: buildStatuteJsonLdBlocks(statute),
  };
}

export default function StatuteRouteSEO() {
  const loc = useLocation();
  const routerPath = normalizePathname(loc.pathname);
  const documentPath =
    typeof document !== 'undefined' ? normalizePathname(document.location.pathname) : routerPath;

  if (typeof document !== 'undefined' && routerPath !== documentPath) {
    return null;
  }

  const path = routerPath;

  if (path === '/statutes') {
    return (
      <SEO
        title={STATUTES_INDEX_SEO.title}
        description={STATUTES_INDEX_SEO.description}
        canonical={STATUTES_INDEX_SEO.canonical}
        jsonLd={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: 'Pakistani Statutes & Laws',
          description: STATUTES_INDEX_SEO.description,
          url: STATUTES_INDEX_SEO.canonical,
          hasPart: Object.values(STATUTES).map((s) => ({
            '@type': 'Legislation',
            name: s.displayTitle,
            url: s.canonical,
          })),
        }}
      />
    );
  }

  const statuteIdMatch = path.match(/^\/statutes\/([^/]+)$/);
  if (statuteIdMatch) {
    const id = statuteIdMatch[1];
    const statute = STATUTES[id];
    if (!statute) return null;

    const seo = resolveStatuteSeo(statute);
    return (
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        jsonLd={seo.jsonLd}
      />
    );
  }

  return null;
}
